import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';
import { PushNotifications } from '@capacitor/push-notifications';
import { supabase } from './supabaseClient';

export class NotificationService {
    private static instance: NotificationService;
    private registration: ServiceWorkerRegistration | null = null;
    private keepAliveInterval: NodeJS.Timeout | null = null;
    private isNative: boolean = Capacitor.isNativePlatform();
    private notificationId: number = 1;
    private fcmToken: string | null = null;
    private currentWorkerId: string | null = null;
    private jobRequestCallback: ((job: any) => void) | null = null;
    private initialized: boolean = false;

    private constructor() {
        if (this.isNative) {
            this.setupPushListeners();
        }
    }

    static getInstance(): NotificationService {
        if (!NotificationService.instance) {
            NotificationService.instance = new NotificationService();
        }
        return NotificationService.instance;
    }

    setJobRequestCallback(callback: (job: any) => void) {
        this.jobRequestCallback = callback;
    }

    // Initialize Service Worker and Capacitor Notifications
    async initialize(): Promise<boolean> {
        // Prevent double initialization
        if (this.initialized) return true;

        // For native apps, request Capacitor notification permissions
        if (this.isNative) {
            try {
                const permStatus = await PushNotifications.requestPermissions();
                if (permStatus.receive === 'granted') {
                    console.log('✅ Push Notifications permission granted');
                    PushNotifications.register();

                    // Create Notification Channels (Android 8.0+)
                    // 1. Job Calls - HIGH PRIORITY (uses device DEFAULT ringtone)
                    await PushNotifications.createChannel({
                        id: 'job_calls',
                        name: 'Job Calls',
                        description: 'Incoming job requests and broadcasts',
                        importance: 5, // IMPORTANCE_HIGH
                        visibility: 1, // VISIBILITY_PUBLIC
                        vibration: true,
                        // 'default' = device default notification sound
                        sound: 'default',
                    });

                    // 2. Job Updates - DEFAULT PRIORITY
                    await PushNotifications.createChannel({
                        id: 'job_updates',
                        name: 'Job Updates',
                        description: 'Job status changes and notifications',
                        importance: 3, // IMPORTANCE_DEFAULT
                        visibility: 1,
                        vibration: true,
                        sound: 'default',
                    });

                    // 3. System Alerts - LOW PRIORITY
                    await PushNotifications.createChannel({
                        id: 'system_alerts',
                        name: 'System Alerts',
                        description: 'Account and system notifications',
                        importance: 2, // IMPORTANCE_LOW
                        visibility: 1,
                        vibration: false,
                        sound: 'default',
                    });

                    console.log('✅ Notification channels created (using device default ringtones)');
                } else {
                    console.warn('⚠️ Push Notification permission denied');
                }

                const localStatus = await LocalNotifications.requestPermissions();
                this.initialized = true;
                if (localStatus.display === 'granted') {
                    return true;
                }
                return false;
            } catch (error) {
                console.error('❌ Capacitor Notifications failed:', error);
                return false;
            }
        }

        // For web, use Service Worker
        if (!('serviceWorker' in navigator)) {
            console.warn('Service Workers not supported');
            return false;
        }

        try {
            // Register service worker
            this.registration = await navigator.serviceWorker.register('/service-worker.js');
            console.log('✅ Service Worker registered:', this.registration);

            // Wait for service worker to be ready
            await navigator.serviceWorker.ready;

            // Request notification permission
            await this.requestPermission();

            // Start keep-alive mechanism to prevent SW from being killed
            this.startKeepAlive();

            // Listen for messages from SW (e.g., notification clicks)
            this.setupServiceWorkerMessageListener();

            this.initialized = true;
            return true;
        } catch (error) {
            console.error('❌ Service Worker registration failed:', error);
            return false;
        }
    }

    // Listen for messages from the Service Worker
    private setupServiceWorkerMessageListener(): void {
        if (this.isNative) return;
        navigator.serviceWorker.addEventListener('message', (event) => {
            if (event.data?.type === 'NOTIFICATION_CLICK') {
                const data = event.data.data;
                // Trigger callback for job notifications
                if (data?.type === 'JOB_REQUEST' && this.jobRequestCallback) {
                    this.jobRequestCallback(data);
                }
                // Navigate if URL provided
                if (event.data.url) {
                    window.location.hash = event.data.url;
                }
            }
        });
    }

    // Request notification permission
    async requestPermission(): Promise<boolean> {
        if (this.isNative) {
            const permStatus = await LocalNotifications.requestPermissions();
            return permStatus.display === 'granted';
        }

        if (!('Notification' in window)) {
            console.warn('Notifications not supported');
            return false;
        }

        if (Notification.permission === 'granted') {
            return true;
        }

        if (Notification.permission !== 'denied') {
            const permission = await Notification.requestPermission();
            return permission === 'granted';
        }

        return false;
    }

    // Show notification using Service Worker (works in BACKGROUND too)
    async showNotification(title: string, options: {
        body: string;
        tag?: string;
        data?: any;
        requireInteraction?: boolean;
        vibrate?: number[];
        isJobNotification?: boolean;
    }): Promise<void> {
        // ===== NATIVE (Capacitor) =====
        if (this.isNative) {
            try {
                await LocalNotifications.schedule({
                    notifications: [
                        {
                            id: this.notificationId++,
                            title: title,
                            body: options.body,
                            largeBody: options.body,
                            summaryText: options.isJobNotification ? 'New Work Available' : 'Hum Hena',
                            // undefined = system default ringtone
                            sound: undefined,
                            ongoing: options.requireInteraction || false,
                            autoCancel: true,
                            extra: options.data,
                            smallIcon: 'ic_notification',
                            iconColor: '#2563EB',
                            // job_calls channel = IMPORTANCE_HIGH = heads-up + device ringtone
                            channelId: options.isJobNotification ? 'job_calls' : 'job_updates',
                        }
                    ]
                });
                console.log('✅ Capacitor Notification shown:', title);
            } catch (error) {
                console.error('❌ Failed to show Capacitor notification:', error);
            }
            return;
        }

        // ===== WEB (Service Worker - works in background) =====
        if (Notification.permission !== 'granted') {
            console.warn('Notification permission not granted');
            return;
        }

        try {
            // ALWAYS use Service Worker to show notifications
            // This ensures they appear even when the tab is in the background or minimized
            const sw = await navigator.serviceWorker.ready;

            if (sw && sw.active) {
                // Send to SW via postMessage for guaranteed background delivery
                sw.active.postMessage({
                    type: 'SHOW_NOTIFICATION',
                    title: title,
                    body: options.body,
                    tag: options.tag || 'notification',
                    vibrate: options.vibrate || [200, 100, 200, 100, 200, 100, 200],
                    requireInteraction: options.requireInteraction !== false,
                    renotify: true, // Re-alert even if same tag exists
                    data: {
                        ...options.data,
                        isJobNotification: options.isJobNotification
                    }
                });
                console.log('✅ SW Notification dispatched:', title);
            } else if (this.registration) {
                // Fallback: Use registration directly
                await this.registration.showNotification(title, {
                    body: options.body,
                    icon: '/logo.png',
                    badge: '/logo.png',
                    tag: options.tag || 'notification',
                    requireInteraction: options.requireInteraction !== false,
                    data: options.data,
                    silent: false, // Use device default notification sound
                    ...({ vibrate: options.vibrate || [200, 100, 200, 100, 200, 100, 200] } as any)
                });
                console.log('✅ Registration Notification shown:', title);
            } else {
                // Last resort: basic Notification API (foreground only)
                new Notification(title, {
                    body: options.body,
                    icon: '/logo.png',
                    tag: options.tag || 'notification',
                    requireInteraction: options.requireInteraction !== false,
                    data: options.data,
                    silent: false
                });
                console.log('✅ Basic Notification shown:', title);
            }
        } catch (error) {
            console.error('❌ Failed to show web notification:', error);
        }
    }

    // Show persistent job notification with device ringtone
    async showJobNotification(jobData: {
        jobId: string;
        title: string;
        customerName: string;
        skill: string;
        isBroadcast?: boolean;
    }): Promise<void> {
        // Trigger In-App Call Screen
        if (this.jobRequestCallback) {
            this.jobRequestCallback(jobData);
        }

        const title = jobData.isBroadcast ? '🔔 New Job Available!' : '📞 Direct Booking Request';
        const body = `${jobData.customerName} needs ${jobData.skill}`;

        await this.showNotification(title, {
            body,
            tag: `job-${jobData.jobId}`,
            requireInteraction: true, // Stays until user clicks
            data: {
                type: 'JOB_REQUEST',
                jobId: jobData.jobId,
                url: '/worker/jobs'
            },
            vibrate: [300, 100, 300, 100, 300, 100, 300], // Longer vibration for jobs
            isJobNotification: true
        });
    }

    // Show message notification with device ringtone
    async showMessageNotification(messageData: {
        senderId: string;
        senderName: string;
        message: string;
    }): Promise<void> {
        const title = `💬 ${messageData.senderName}`;
        const body = messageData.message;

        await this.showNotification(title, {
            body,
            tag: `message-${messageData.senderId}`,
            requireInteraction: false,
            data: {
                type: 'MESSAGE',
                senderId: messageData.senderId
            },
            vibrate: [200, 100, 200],
            isJobNotification: false
        });
    }

    // Close specific notification
    async closeNotification(tag: string): Promise<void> {
        if (this.isNative) {
            return;
        }

        if (!this.registration) return;

        const notifications = await this.registration.getNotifications({ tag });
        notifications.forEach(notification => notification.close());
    }

    // Keep service worker alive (prevents it from being killed in background)
    private startKeepAlive(): void {
        if (this.isNative) return;

        if (this.keepAliveInterval) {
            clearInterval(this.keepAliveInterval);
        }

        // Ping service worker every 20 seconds to keep it alive
        this.keepAliveInterval = setInterval(() => {
            if (navigator.serviceWorker.controller) {
                const messageChannel = new MessageChannel();
                messageChannel.port1.onmessage = (event) => {
                    if (event.data?.type === 'ALIVE') {
                        // SW is alive, good
                    }
                };
                navigator.serviceWorker.controller.postMessage(
                    { type: 'KEEP_ALIVE' },
                    [messageChannel.port2]
                );
            }
        }, 20000);
    }

    // Stop keep-alive
    stopKeepAlive(): void {
        if (this.keepAliveInterval) {
            clearInterval(this.keepAliveInterval);
            this.keepAliveInterval = null;
        }
    }

    // Check if app is running in background
    isInBackground(): boolean {
        return document.hidden || document.visibilityState === 'hidden';
    }

    // Listen for visibility changes
    onVisibilityChange(callback: (isBackground: boolean) => void): () => void {
        const handler = () => {
            callback(this.isInBackground());
        };

        document.addEventListener('visibilitychange', handler);

        return () => {
            document.removeEventListener('visibilitychange', handler);
        };
    }

    private setupPushListeners() {
        PushNotifications.addListener('registration', async (token: any) => {
            console.log('Push registration success, token: ' + token.value);
            this.fcmToken = token.value;
            if (this.currentWorkerId) {
                await this.saveTokenToSupabase(this.currentWorkerId, token.value);
            }
        });

        PushNotifications.addListener('registrationError', (error: any) => {
            console.error('Error on push registration: ' + JSON.stringify(error));
        });

        PushNotifications.addListener('pushNotificationReceived', (notification: any) => {
            console.log('Push received: ' + JSON.stringify(notification));
            const data = notification.data;
            if (data && (data.title || data.body)) {
                this.showNotification(data.title || 'New Notification', {
                    body: data.body || '',
                    data: data,
                    isJobNotification: !!data.jobId,
                    requireInteraction: true
                });
            }
        });

        PushNotifications.addListener('pushNotificationActionPerformed', (notification: any) => {
            console.log('Push action performed: ' + JSON.stringify(notification));
            const data = notification.notification.data;
            const actionId = notification.actionId;

            // Handle Accept/Reject actions
            if (actionId === 'ACCEPT_JOB' && data && data.jobId) {
                console.log('✅ Job accepted via notification action:', data.jobId);
                if (data.type === 'JOB_REQUEST' && this.jobRequestCallback) {
                    this.jobRequestCallback(data);
                }
                window.location.hash = `/worker/jobs`;
            } else if (actionId === 'REJECT_JOB' && data && data.jobId) {
                console.log('❌ Job rejected via notification action:', data.jobId);
            } else if (data && data.jobId) {
                if (data.type === 'JOB_REQUEST' && this.jobRequestCallback) {
                    this.jobRequestCallback(data);
                }
                window.location.hash = `/worker/jobs`;
            }
        });
    }

    private async saveTokenToSupabase(workerId: string, token: string) {
        try {
            const { error } = await supabase
                .from('worker_fcm_tokens')
                .upsert({ worker_id: workerId, token: token }, { onConflict: 'worker_id' });

            if (error) throw error;
            console.log('✅ FCM Token saved via Supabase');
        } catch (err) {
            console.error('❌ Failed to save FCM token:', err);
        }
    }

    public async syncWorkerToken(workerId: string) {
        this.currentWorkerId = workerId;
        if (this.fcmToken) {
            await this.saveTokenToSupabase(workerId, this.fcmToken);
            await this.syncTokenToBackend(workerId, this.fcmToken);
        }
    }

    private async syncTokenToBackend(workerId: string, token: string) {
        try {
            const apiUrl = '/api/register_token.php';
            await fetch(apiUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ worker_id: workerId, token: token })
            });
            console.log('✅ FCM Token synced to PHP Backend');
        } catch (error) {
            console.error('❌ Failed to sync token to PHP Backend:', error);
        }
    }
}

// Export singleton instance
export const notificationService = NotificationService.getInstance();
